import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ReactPlayer from 'react-player';
import GoogleContentPreviewButtons from './GoogleContentPreviewButtons';
import type { GoogleCreativeFormat } from '../api/types';

interface IProps {
  creativeId: string;
  adLibraryUrl: string;
  contentUrl?: string;
  previewUrl?: string;
  /** video poster shown before playback — paired with contentUrl */
  posterUrl?: string;
  mediaType: GoogleCreativeFormat | 'image';
  containerId?: string;
  onLoad?: () => void;
  hideButtons?: boolean;
  isActive?: boolean;
  autoHeight?: boolean;
  onPlayingChange?: (isPlaying: boolean) => void;
}

const GoogleContentPreview: FC<IProps> = ({
  creativeId,
  adLibraryUrl,
  contentUrl,
  previewUrl,
  posterUrl,
  mediaType,
  containerId,
  onLoad,
  hideButtons,
  isActive = true,
  autoHeight = false,
  onPlayingChange,
}) => {
  const [isError, setIsError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerKey, setPlayerKey] = useState(0);
  // an anonymous load sends an Origin header, so the CDN caches a copy with CORS headers and the creative
  // stays downloadable as a blob — assets whose cached copy has no CORS headers are reloaded without it
  const [isAnonymousLoad, setIsAnonymousLoad] = useState(true);

  const isVideo = mediaType === 'video' && !!contentUrl;

  useEffect(() => {
    if (!isActive) {
      setIsPlaying(false);
      setPlayerKey((key) => key + 1);
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      onPlayingChange?.(isPlaying);
    }
  }, [isActive, isPlaying, onPlayingChange]);

  if (isError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          gap: 2,
        }}
      >
        <Typography color="text.secondary" variant="body2">
          Content unavailable
        </Typography>
      </Box>
    );
  }

  if (isVideo) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          bgcolor: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <ReactPlayer
          key={playerKey}
          src={contentUrl}
          light={posterUrl ?? true}
          playing={isPlaying}
          muted={!isPlaying}
          controls={isPlaying}
          playsInline
          width="100%"
          height="100%"
          onReady={onLoad}
          onClickPreview={() => {
            setIsPlaying(true);
          }}
          onPause={() => {
            setIsPlaying(false);
          }}
          onEnded={() => {
            setIsPlaying(false);
          }}
          onError={() => {
            setIsError(true);
          }}
          style={{ objectFit: 'contain' }}
        />
        {!hideButtons && (
          <GoogleContentPreviewButtons
            mediaType={mediaType}
            creativeId={creativeId}
            adLibraryUrl={adLibraryUrl}
            contentUrl={contentUrl}
            previewUrl={previewUrl}
            containerId={containerId}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: autoHeight ? 'auto' : '100%',
        minHeight: autoHeight ? 150 : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        onLoad={onLoad}
        onError={() => {
          if (isAnonymousLoad) {
            setIsAnonymousLoad(false);
            return;
          }
          setIsError(true);
        }}
        loading="lazy"
        crossOrigin={isAnonymousLoad ? 'anonymous' : undefined}
        src={contentUrl}
        alt="preview"
        sx={{
          position: autoHeight ? 'relative' : 'absolute',
          width: '100%',
          height: autoHeight ? 'auto' : '100%',
          objectFit: 'contain',
          display: 'block',
          borderRadius: 0,
          backgroundColor: '#FFF',
        }}
      />
      {!hideButtons && (
        <GoogleContentPreviewButtons
          mediaType={mediaType}
          creativeId={creativeId}
          adLibraryUrl={adLibraryUrl}
          contentUrl={contentUrl}
          previewUrl={previewUrl}
          containerId={containerId}
        />
      )}
    </Box>
  );
};

export default GoogleContentPreview;
